import axios from 'axios';
import { Article, PaginatedResponse, ArticleFilters, Category, Tag, Stats, Author, AuthorWithArticles, DailyBrief, BriefListItem, Opportunity, OpportunitySummary, OpportunityFilters } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const articleApi = {
  getArticles: async (filters: ArticleFilters = {}): Promise<PaginatedResponse<Article>> => {
    const response = await api.get('/articles', { params: filters });
    return response.data;
  },

  getArticleBySlug: async (slug: string): Promise<Article> => {
    const response = await api.get(`/articles/${slug}`);
    return response.data;
  },

  getRelatedArticles: async (id: string, limit: number = 5): Promise<Article[]> => {
    const response = await api.get(`/articles/${id}/related`, { params: { limit } });
    return response.data;
  },

  getTrendingArticles: async (limit: number = 10): Promise<Article[]> => {
    const response = await api.get('/articles/trending', { params: { limit } });
    return response.data;
  },
};

export const authorApi = {
  getAuthors: async (): Promise<Author[]> => {
    const response = await api.get('/authors');
    return response.data;
  },

  getAuthorBySlug: async (slug: string): Promise<AuthorWithArticles> => {
    const response = await api.get(`/authors/${slug}`);
    return response.data;
  },
};

export const briefApi = {
  getLatest: async (): Promise<DailyBrief> => {
    const response = await api.get('/briefs/latest');
    return response.data;
  },

  getByDate: async (date: string): Promise<DailyBrief> => {
    const response = await api.get(`/briefs/${date}`);
    return response.data;
  },

  getArchive: async (limit = 30): Promise<BriefListItem[]> => {
    const response = await api.get('/briefs', { params: { limit } });
    return response.data;
  },
};

export const opportunityApi = {
  getOpportunities: async (filters: OpportunityFilters = {}): Promise<OpportunitySummary[]> => {
    const response = await api.get('/opportunities', { params: filters });
    return response.data;
  },

  getOpportunityBySlug: async (slug: string): Promise<Opportunity> => {
    const response = await api.get(`/opportunities/${slug}`);
    return response.data;
  },

  reportProblem: async (slug: string, reason: string, details?: string): Promise<void> => {
    await api.post(`/opportunities/${slug}/report`, { reason, details });
  },
};

export const metadataApi = {
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/metadata/categories');
    return response.data;
  },

  getTags: async (): Promise<Tag[]> => {
    const response = await api.get('/metadata/tags');
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get('/metadata/stats');
    return response.data;
  },
};

export default api;
